import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, DiscordIcon, EmptyState } from '@playerbase/ui';

import { useShell } from '../shell/use-shell.js';
import {
  startDiscordConnect,
  type StartDiscordConnectError,
} from '../games/discord-data.js';

// Post-game-creation onboarding step. Story:
//   "Onboarding prompt - immediately after a game is created, the user sees
//    a *Connect a Discord server* CTA with a *Connect later* skip option.
//    Skipping doesn't block anything; the user lands on whatever the next
//    progressive setup step is."
//
// Today the next step after Discord is the rest of Game settings, so Skip
// goes straight to /game-settings/overview. Connect kicks off the same
// OAuth flow as the Discord connection card and ends up here on return.

function startErrorMessage(code: StartDiscordConnectError): string {
  switch (code) {
    case 'not_authenticated':
      return 'Sign in again and retry.';
    case 'not_a_member':
      return 'You are not a member of this studio.';
    case 'game_not_found':
      return 'This game no longer exists.';
    case 'server_misconfigured':
      return 'Discord OAuth is not configured for this environment.';
    default:
      return 'Could not start the Discord connection. Try again.';
  }
}

export function WelcomeDiscordPage(): JSX.Element {
  const { activeStudio, activeGame } = useShell();
  const { studioShortId, gameShortId } = useParams<{ studioShortId: string; gameShortId: string }>();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skip = useCallback((): void => {
    navigate(`/s/${studioShortId ?? ''}/g/${gameShortId ?? ''}/game-settings/overview`);
  }, [navigate, studioShortId, gameShortId]);

  const handleConnect = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setError('No active game in context. Reload the page and try again.');
      return;
    }
    setStarting(true);
    setError(null);
    const result = await startDiscordConnect({
      studioId: activeStudio.id,
      gameId: activeGame.id,
    });
    setStarting(false);
    if (!result.ok) {
      setError(startErrorMessage(result.code));
      return;
    }
    window.location.href = result.url;
  }, [activeStudio.id, activeGame]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <header className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-wider text-text-muted">
          {activeStudio.name} {activeGame !== null ? `/ ${activeGame.name}` : ''}
        </p>
        <h1 className="mt-2 font-display text-2xl font-bold text-text">
          Connect a Discord server
        </h1>
        <p className="mt-2 text-sm text-text-muted">
          PlayerBase watches Discord channels you choose for player feedback. To
          start, install the bot in your community server. You can do this now or
          later from Discord connection.
        </p>
      </header>

      <EmptyState
        eyebrow="Step 1 of setup"
        title={activeGame !== null ? `Bring ${activeGame.name} online` : 'Bring this game online'}
        description="One click adds the PlayerBase bot to a Discord server you already manage. You will be redirected to Discord to pick the server."
        cta={
          <Button variant="discord" size="md" onClick={() => void handleConnect()} disabled={starting}>
            <DiscordIcon className="h-4 w-auto" />
            {starting ? 'Opening Discord…' : 'Connect a Discord server'}
          </Button>
        }
        footnote="You need Manage Server permission on the target server."
      />

      {error !== null && (
        <Alert tone="danger" className="mt-4">
          {error}
        </Alert>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button asChild variant="ghost" size="md">
          <Link to={`/s/${studioShortId ?? ''}/g/${gameShortId ?? ''}/game-settings/overview`}>
            Connect later
          </Link>
        </Button>
        <Button variant="ghost" size="sm" onClick={skip}>
          Skip for now
        </Button>
      </div>
    </div>
  );
}
